import { gql } from '@apollo/client';
import {
    USER_FIELDS_FRAGMENT,
    USER_PROJECT_ROLE_TYPE_FRAGMENT
} from '../../../../lib/apollo/fragments/user';
import { MACHINE_SIMPLE_FIELDS } from '../../../../lib/apollo/fragments/fleet';
import { FR_INSPECTION_LIST_NAME_ID_FIELDS } from '../../../../lib/apollo/fragments/inspection';

const SIMPLE_EPIC_FRAGMENT = gql`
    fragment SimpleEpicFields on Epic {
        EpicId
        CoordinateSystemType {
            CoordinateSystemTypeId
            Name
            DatumCode
        }
        HeightSystemType {
            HeightSystemTypeId
            Name
        }
    }
`;

export const SIMPLE_TASKS_WITH_LOCATIONS_FRAGMENT = gql`
    fragment SimpleTasksWithLocations on Task {
        TaskId
        Location {
            LocationId
        }
    }
`;

export const TASK_SUMMARY_FRAGMENT = gql`
    fragment TaskSummaryFields on TaskSummary {
        TaskType {
            TaskTypeId
            Name
            Abbr
            TaskGroup
        }
        TaskStatus {
            TaskStatusId
            Name
        }
        Open
        Assigned
        Resourced
        Ongoing
        Done
        Cancelled
        Failed
        Paused
        Total
    }
`;

export const PROJECTS_CONTAINER_PROJECTS_FRAGMENT = gql`
    fragment ProjectsContainerProjects on Project {
        ProjectId
        Name
        ProjectNbr
        PossibleStartDate
        CableShowingDate
        Addr
        Creator
        Created
        Updated
        ProjectHSEStatics {
            TotalAccidents
            TotalNearMisses
            TotalDeviations
            TotalAlarms
            TotalObservations
            TotalDamages
            AssessmentCount
            FailedAssessmentCount
        }
        Access {
            Read
            Create
            Delete
            Update
        }
        ProjectSummary {
            TaskSummary {
                ...TaskSummaryFields
            }
            TaskDone
            TaskTotal
        }
        Tasks {
            ...SimpleTasksWithLocations
            TaskStatus {
                Name
            }
            EndDate
        }
        ProjectInspection {
            ProjectInspectionId
            InspectionList {
                ...fr_InspectionListNameIdFields
            }
        }
        InspectionEntries {
            InspectionEntryId
            InspectionList {
                InspectionListId
            }
            InspectionEntryStatus {
                Name
            }
        }
        Epic {
            ...SimpleEpicFields
        }
        WorkPackage {
            WorkPackageId
            Access {
                Read
                Create
                Delete
                Update
            }
            Instructions
            Cables
            Pipes
            Lines
            LocationMoveAllowedAtObstacles
            MaxLocationMove
            AgreeLocationMoveWithDesigner
            DataSource {
                WorkPackageDataSourceId
                Token
                Url
            }
            TaskTypeInstruction {
                TaskTypeInstructionId
                Instruction
                Created
                TaskType {
                    Name
                    TaskTypeId
                }
                Updated
                WorkPackage {
                    WorkPackageId
                }
            }
        }
        ProjectType {
            ProjectTypeId
            Name
        }
        ProjectStatus {
            ProjectStatusId
            Name
        }
        ProjectUsers {
            User {
                UserState
                Firstname
                Lastname
                Phone
                Email
                UserId
                OwnOrganization {
                    OrganizationId
                    Name
                }
            }
            AccessUserType {
                ... on UserProjectRoleType {
                    UserProjectRoleTypeId
                    Name
                }
            }
        }
        Machines {
            ...MachineSimpleInfoFields
        }
    }
    ${SIMPLE_TASKS_WITH_LOCATIONS_FRAGMENT}
    ${TASK_SUMMARY_FRAGMENT}
    ${SIMPLE_EPIC_FRAGMENT}
    ${MACHINE_SIMPLE_FIELDS}
    ${FR_INSPECTION_LIST_NAME_ID_FIELDS}
`;

export const PROJECTS_CONTAINER_EPICS_FRAGMENT = gql`
    fragment ProjectsContainerEpics on Epic {
        ...SimpleEpicFields
        Name
        EpicNbr
        Creator
        Created
        Updated
        Access {
            Read
            Create
            Delete
            Update
        }
        EpicStatus {
            EpicStatusId
            Name
        }
        EpicType {
            EpicTypeId
            Name
        }
        Secret
        Tasks {
            ...SimpleTasksWithLocations
            TaskStatus {
                Name
            }
            EndDate
        }
        EpicSummary {
            WorkPackageDone
            WorkPackageTotal
            TaskDone
            TaskTotal
            TaskSummary {
                ...TaskSummaryFields
            }
        }
        EpicUsers {
            User {
                UserState
                Firstname
                Lastname
                Phone
                Email
                UserId
                OwnOrganization {
                    OrganizationId
                    Name
                }
            }
            AccessUserType {
                ... on UserEpicRoleType {
                    UserEpicRoleTypeId
                    Name
                }
            }
        }
        WorkPackage {
            WorkPackageId
            Access {
                Read
                Create
                Delete
                Update
            }
            DataSource {
                WorkPackageDataSourceId
                Url
                Token
            }
            TaskTypeInstruction {
                TaskTypeInstructionId
            }
        }
    }
    ${SIMPLE_TASKS_WITH_LOCATIONS_FRAGMENT}
    ${TASK_SUMMARY_FRAGMENT}
    ${SIMPLE_EPIC_FRAGMENT}
`;

export const PROJECTS_CONTAINER_WORKPACKAGES_FRAGMENT = gql`
    fragment ProjectsContainerWorkPackages on WorkPackage {
        WorkPackageId
        WorkName
        WorkNbr
        Access {
            Read
            Create
            Delete
            Update
        }
        DataSource {
            WorkPackageDataSourceId
            Url
            Token
        }
        Instructions
        Cables
        Pipes
        Lines
        LocationMoveAllowedAtObstacles
        MaxLocationMove
        AgreeLocationMoveWithDesigner
        Created
        Updated
        Epic {
            ...SimpleEpicFields
        }
        Tasks {
            ...SimpleTasksWithLocations
            TaskStatus {
                Name
            }
            EndDate
        }
        WorkPackageSummary {
            TaskDone
            TaskTotal
            TaskSummary {
                ...TaskSummaryFields
            }
        }
        Project {
            ProjectId
            Name
            ProjectNbr
            Access {
                Read
                Create
                Delete
                Update
            }
            ProjectSummary {
                TaskSummary {
                    ...TaskSummaryFields
                }
                TaskDone
                TaskTotal
            }
            Tasks {
                ...SimpleTasksWithLocations
                TaskStatus {
                    Name
                }
            }
            ProjectUsers {
                User {
                    ...UserFields
                }
                AccessUserType {
                    __typename
                    ... on UserProjectRoleType {
                        ...UserProjectRoleTypeFields
                    }
                }
            }
        }
        WorkPackageStatus {
            WorkPackageStatusId
            Name
        }
        WorkPackageUsers {
            User {
                UserState
                Firstname
                Lastname
                Phone
                Email
                UserId
                OwnOrganization {
                    OrganizationId
                    Name
                }
            }
            AccessUserType {
                ... on UserWorkPackageRoleType {
                    UserWorkPackageRoleTypeId
                    Name
                }
            }
        }
        TaskTypeInstruction {
            TaskTypeInstructionId
            Instruction
            Created
            TaskType {
                Name
                TaskTypeId
            }
            Updated
            WorkPackage {
                WorkPackageId
            }
        }
    }
    ${SIMPLE_TASKS_WITH_LOCATIONS_FRAGMENT}
    ${TASK_SUMMARY_FRAGMENT}
    ${SIMPLE_EPIC_FRAGMENT}
    ${USER_FIELDS_FRAGMENT}
    ${USER_PROJECT_ROLE_TYPE_FRAGMENT}
`;
